// Small, bounded SVG geometry contract shared by the CMS and native renderer.
// No URLs, style, markup, handlers or arbitrary React properties cross it.
const attributes = {
  path: ['d'], circle: ['cx', 'cy', 'r'], ellipse: ['cx', 'cy', 'rx', 'ry'],
  line: ['x1', 'y1', 'x2', 'y2'], polyline: ['points'], polygon: ['points'],
  rect: ['x', 'y', 'width', 'height', 'rx', 'ry'],
} as const;
export type IconNode = [keyof typeof attributes, Record<string, string | number>][];
export function isIconNode(value: unknown): value is IconNode {
  if (!Array.isArray(value) || !value.length || value.length > 64) return false;
  try { if (JSON.stringify(value).length > 16384) return false; } catch { return false; }
  return value.every(node => {
    if (!Array.isArray(node) || node.length !== 2 || typeof node[0] !== 'string'
      || !Object.prototype.hasOwnProperty.call(attributes, node[0])) return false;
    const allowed: readonly string[] = attributes[node[0] as keyof typeof attributes];
    const attrs = node[1];
    if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs)) return false;
    const keys = Object.keys(attrs);
    if (!keys.length || keys.some(key => key !== 'fill' && !allowed.includes(key))) return false;
    return keys.every(key => {
      const data = attrs[key];
      if (key === 'fill') return data === 'none' || data === 'currentColor';
      if (key === 'd') return typeof data === 'string' && data.length > 0 && data.length <= 8192 && /^[MmZzLlHhVvCcSsQqTtAaEe0-9+.,\s-]+$/.test(data);
      if (key === 'points') return typeof data === 'string' && data.length > 0 && data.length <= 8192 && /^[0-9+.,\sEe-]+$/.test(data);
      return (typeof data === 'number' && Number.isFinite(data) && Math.abs(data) <= 1024)
        || (typeof data === 'string' && data.length <= 32 && /^[-+]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][-+]?\d+)?$/.test(data) && Math.abs(Number(data)) <= 1024);
    });
  });
}
